import React from 'react';
import { CookieRecord } from '../utils/types';
import { T, COOKIE_CATEGORY } from '../designTokens';

interface CookieTableProps {
  cookies: CookieRecord[];
  initialFilter?: string;
}

/** Human-readable expiry: session, or duration from now. */
function formatExpiry(expires: number): string {
  if (expires < 0) return 'Session';
  const seconds = expires - Date.now() / 1000;
  if (seconds <= 0) return 'Expired';
  const days = seconds / 86400;
  if (days >= 365) return `${Math.round(days / 365)} year${Math.round(days / 365) === 1 ? '' : 's'}`;
  if (days >= 30) return `${Math.round(days / 30)} month${Math.round(days / 30) === 1 ? '' : 's'}`;
  if (days >= 1) return `${Math.round(days)} day${Math.round(days) === 1 ? '' : 's'}`;
  const hours = Math.max(1, Math.round(seconds / 3600));
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

export default function CookieTable({ cookies, initialFilter = '' }: CookieTableProps) {
  const [searchTerm, setSearchTerm] = React.useState(initialFilter);
  if (cookies.length === 0) {
    return (
      <div style={{ padding: 20 }}>
        <p style={{ color: T.gray500 }}>No cookies were set during the scan.</p>
      </div>
    );
  }

  const filteredCookies = searchTerm
    ? cookies.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        c.domain.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : cookies;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <input 
          type="text" 
          placeholder="Filter cookies by name or domain..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', border: `1px solid ${T.gray300}`, borderRadius: 6, fontFamily: T.fontBody, fontSize: 14 }}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ backgroundColor: T.gray50 }}>
              <th style={thStyle}>Cookie</th>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Domain</th>
              <th style={thStyle}>
                <span title="First-party cookies are set by the scanned site; third-party cookies are set by other domains">
                  Party
                </span>
              </th>
              <th style={thStyle}>Expiry</th>
              <th style={thStyle}>Purpose</th>
            </tr>
          </thead>
          <tbody>
            {filteredCookies.map((cookie, i) => {
              const cat = COOKIE_CATEGORY[cookie.category] ?? COOKIE_CATEGORY.uncategorized;
              return (
                <tr
                  key={`${cookie.name}|${cookie.domain}`}
                  style={{
                    backgroundColor: i % 2 === 0 ? T.white : T.gray50,
                    borderBottom: `1px solid ${T.gray200}`,
                  }}
                >
                  <td style={tdStyle}>
                    <code style={{ fontSize: 13, fontFamily: T.fontMono, color: T.gray700 }}>
                      {cookie.name}
                    </code>
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        backgroundColor: cat.bg,
                        color: cat.color,
                        fontWeight: 600,
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cat.label}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <code style={{ fontSize: 13, fontFamily: T.fontMono, color: T.gray500 }}>
                      {cookie.domain}
                    </code>
                  </td>
                  <td style={tdStyle}>
                    {cookie.firstParty ? (
                      <span style={{ color: T.gray600 }}>First-party</span>
                    ) : (
                      <span style={{ color: T.warning, fontWeight: 500 }}>Third-party</span>
                    )}
                  </td>
                  <td style={tdStyle}>{formatExpiry(cookie.expires)}</td>
                  <td style={{ ...tdStyle, maxWidth: 320 }}>
                    {cookie.vendor || cookie.description ? (
                      <div style={{ fontSize: 13, color: T.gray500 }}>
                        {cookie.vendor && (
                          <span style={{ fontWeight: 500, color: T.gray600 }}>{cookie.vendor}</span>
                        )}
                        {cookie.vendor && cookie.description && ' — '}
                        {cookie.description}
                      </div>
                    ) : (
                      <span style={{ color: T.gray300 }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px',
  fontWeight: 600,
  color: T.gray500,
  borderBottom: `2px solid ${T.gray200}`,
  fontSize: 12,
  fontFamily: T.fontBody,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  color: T.gray700,
};
